import { StorageKeys } from "@utils/enums";

import { WishlistModel } from "@redux/modules/api/data/wishlist/@types/wishlist.model";
import { TTranslation } from "@redux/modules/value/translations/translations.types";
import { translateKey } from "@redux/modules/value/translations/translations.helpers";

import { ProductImpressionModel } from "./tracking";
import { MAX_WISHLIST_ITEMS } from "@components/Wishlist/wishlist.config";

const LOCAL_WISHLIST_KEY = StorageKeys.LocalWishlist;

type LocalWishlistItem = {
    sku: string;
    trackingProduct: ProductImpressionModel;
};
type LocalWishlist = Array<LocalWishlistItem>;

const parseEntries = (array: LocalWishlist): LocalWishlist => {
    return array.slice(0, MAX_WISHLIST_ITEMS);
};

const isFull = (wishlist: LocalWishlist): boolean =>
    wishlist.length >= MAX_WISHLIST_ITEMS;

const addProductDirectly = (
    wishlist: LocalWishlist,
    sku: string,
    trackingProduct: ProductImpressionModel,
): LocalWishlist => {
    const newWishlist = [{ sku: sku, trackingProduct }, ...wishlist];
    localStorage.setItem(LOCAL_WISHLIST_KEY, JSON.stringify(newWishlist));

    return newWishlist;
};

export const isWishlistFull = () => isFull(getLocalWishlist());

export const hasLocalWishList = () =>
    !!localStorage.getItem(LOCAL_WISHLIST_KEY);

export const createLocalWishlist = (
    sku: string,
    trackingProduct: ProductImpressionModel,
) => {
    const wishlist: LocalWishlist = [{ sku: sku, trackingProduct }];
    localStorage.setItem(LOCAL_WISHLIST_KEY, JSON.stringify(wishlist));

    return wishlist;
};

export const addProductToLocalWishlist = (
    sku: string,
    trackingProduct: ProductImpressionModel,
): LocalWishlist => {
    if (!hasLocalWishList()) {
        return createLocalWishlist(sku, trackingProduct);
    }

    const wishlist = getLocalWishlist(sku);

    return isFull(wishlist)
        ? wishlist
        : addProductDirectly(wishlist, sku, trackingProduct);
};

export const getLocalWishlist = (omitSku?: string): LocalWishlist => {
    const localStorageItem = localStorage.getItem(LOCAL_WISHLIST_KEY);
    if (!localStorageItem) {
        return [];
    } else {
        const localWishlistArray: LocalWishlist = JSON.parse(localStorageItem);
        if (omitSku) {
            return parseEntries(localWishlistArray).filter(
                (product: LocalWishlistItem) => product.sku !== omitSku,
            );
        }

        return parseEntries(localWishlistArray);
    }
};

export const removeProductFromLocalWishlist = (sku: string): LocalWishlist => {
    const localStorageItem = localStorage.getItem(LOCAL_WISHLIST_KEY);

    if (!localStorageItem) {
        return [];
    }

    const localWishlistArray: LocalWishlist = JSON.parse(localStorageItem);
    const newArray = localWishlistArray.filter(product => product.sku !== sku);
    localStorage.setItem(LOCAL_WISHLIST_KEY, JSON.stringify(newArray));

    return newArray;
};

export const removeLocalWishlist = () => {
    if (localStorage.getItem(LOCAL_WISHLIST_KEY)) {
        localStorage.removeItem(LOCAL_WISHLIST_KEY);
    }
};

export const getNewName = (
    list: Array<WishlistModel>,
    translations: TTranslation,
) => {
    const baseName =
        translateKey(translations, "wishlist.wishlistName") || "Verlanglijstje";
    if (list.length === 0) {
        return baseName;
    }

    const hasName = (name: string) => list.some(l => l.name === name);

    const generate = (name: string, index: number): string => {
        const newName = `${name} - ${index}`;
        if (!hasName(newName)) {
            return newName;
        }

        return generate(baseName, index + 1);
    };

    return hasName(baseName) ? generate(baseName, 1) : baseName;
};
